import { Link, Typography } from '@mui/material';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid/models';
import { useCallback, useMemo, useState } from 'react';
import { OrderSummary } from 'api/models/Orders';
import theme from 'App.theme';
import Button from 'components/Button';
import { NamedSelectOption } from 'components/DropDown/ValidatingSelect';
import SearchInput from 'components/SearchInput';
import { StyledDataGrid } from 'components/StyledDataGrid/StyledDataGrid';
import { useLanguage } from 'context/AppContext';
import { replaceTimeInfoWithUtcInDate } from 'utils/helpers/DateTimeFormat.helpers';
import { useReceiveDeliveryFields } from '../hooks/useReceiveDeliveryFields';
import { Supplier } from 'api/models/Suppliers';

type RowProps = {
  id: number;
  surgeryReference: string;
  patientInitials: string;
  orderNumber: string;
  deliveryNumber: string;
  supplierName: string;
  surgeonName: string;
  surgeryDate: string;
  surgeryDescription: string;
  surgeryComments: string;
};

type Props = {
  orders: OrderSummary[];
  supplierOptions: NamedSelectOption[];
  onContinue: (deliveryId?: number) => void;
  isLoading: boolean;
  selectedSupplier?: Supplier;
};

const DeliverySelect = ({ orders, supplierOptions, onContinue, isLoading, selectedSupplier }: Props) => {
  const lang = useLanguage()[0].ReceiveDeliveryDialog;

  const { fields } = useReceiveDeliveryFields();

  const [searchTerm, setSearchTerm] = useState('');

  const formatSurgeryDate = useCallback(
    (surgeryDate: string | Date) => replaceTimeInfoWithUtcInDate(surgeryDate).split('T')[0],
    [],
  );

  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);

  const onRowSelectionModelChange = useCallback(
    (selection: GridRowSelectionModel) => {
      setRowSelectionModel(selection[0] == rowSelectionModel[0] ? [] : selection);
    },
    [rowSelectionModel],
  );

  const selectedDeliveryId = rowSelectionModel[0] as number | undefined;

  const [memoizedRows, totalRows] = useMemo(() => {
    const searchTermLc = searchTerm.toLowerCase();
    let selectedDeliveryIdIsDisplayed = false;
    let totalDelieveries = 0;

    const deliveries = orders.reduce<RowProps[]>((acc, order) => {
      const dispatchedDeliveries = order.deliveries.filter(d => d.deliveryStage == 'OUTBOUND');

      if (dispatchedDeliveries.length) {
        const surgeryReferenceLc = order.surgeryReference?.toLowerCase() || '';
        const patientInitialsLc = order.patientInitials?.toLowerCase() || '';
        const orderNumberLc = order.orderNumber.toLowerCase();
        const supplierName = supplierOptions.find(s => s.id == order.tenantId)?.label || '';

        dispatchedDeliveries.forEach(delivery => {
          totalDelieveries++;

          const searchMatches =
            !searchTerm ||
            surgeryReferenceLc.includes(searchTermLc) ||
            patientInitialsLc.includes(searchTermLc) ||
            orderNumberLc.includes(searchTermLc) ||
            delivery.deliveryNumber.toLowerCase().includes(searchTermLc);
          if (!searchMatches) return;

          if (selectedDeliveryId == delivery.deliveryId) {
            selectedDeliveryIdIsDisplayed = true;
          }

          acc.push({
            id: delivery.deliveryId,
            //status: delivery.deliveryStage,
            surgeryReference: order.surgeryReference || '',
            orderNumber: order.orderNumber,
            deliveryNumber: delivery.deliveryNumber,
            supplierName,
            surgeonName: order.surgeonName,
            surgeryDate: formatSurgeryDate(order.surgeryDate),
            patientInitials: order.patientInitials || '',
            surgeryDescription: order.surgeryDescription || '',
            surgeryComments: order.surgeryComments || '',
          });
        });
      }

      return acc;
    }, []);

    // if the selected delivery is filtered out, clear the selection
    if (selectedDeliveryId && !selectedDeliveryIdIsDisplayed) {
      setRowSelectionModel([]);
    }

    return [deliveries, totalDelieveries];
  }, [formatSurgeryDate, orders, searchTerm, selectedDeliveryId, supplierOptions]);

  const memoizedColumns = useMemo(() => {
    const colKeys: (keyof RowProps)[] = [
      'surgeryReference',
      'orderNumber',
      'deliveryNumber',
      'supplierName',
      'surgeonName',
      'surgeryDate',
      'patientInitials',
      'surgeryDescription',
      'surgeryComments',
    ];
    const columns: GridColDef[] = colKeys.map((key, idx) => ({
      field: key,
      headerName: lang.ordersTable[key],
      align: 'center',
      headerAlign: 'center',
      minWidth: 120,
      sortable: idx < 3 || idx > 5,
    }));
    return columns;
  }, [lang.ordersTable]);

  const handleContinue = useCallback(() => {
    onContinue(selectedDeliveryId);
  }, [onContinue, selectedDeliveryId]);

  const handleDeliveryNotFound = useCallback(() => {
    onContinue();
  }, [onContinue]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        marginTop: theme.layout.spacing.medium,
        gap: theme.layout.spacing.medium,
      }}
    >
      {totalRows > 3 && <SearchInput onChange={setSearchTerm} />}

      {memoizedRows.length ? (
        <StyledDataGrid
          sx={{ maxHeight: '308px' }}
          hidePagination
          pagination
          disableColumnMenu
          columns={memoizedColumns}
          rows={memoizedRows}
          rowHeight={theme.sizes.rowHeight}
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={onRowSelectionModelChange}
          getRowId={row => row.id}
        />
      ) : (
        <Typography variant="subtitle2" color="error">
          {lang.noResultsFound
            .replace('{supplierName}', fields.supplier?.label || '')
            .replace('{surgeryDate}', fields.surgeryDate ? formatSurgeryDate(fields.surgeryDate) : '')
            .replace('{surgeonName}', fields.surgeon?.label || '')}
        </Typography>
      )}

      <Button
        label={selectedDeliveryId ? lang.receiveSelectedDelivery : lang.selectDeliveryAbove}
        disabled={!selectedDeliveryId || isLoading}
        loading={isLoading}
        colors="white on active"
        onClick={handleContinue}
        sx={{ height: '40px', width: '100%' }}
      />

      {selectedSupplier && (
        <Link sx={{ alignSelf: 'center', cursor: 'pointer', fontSize: '0.8em' }} onClick={handleDeliveryNotFound}>
          {lang.deliveryNotFound}
        </Link>
      )}
    </div>
  );
};

export default DeliverySelect;
